import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePlatform } from "../context/PlatformContext";
import { LayoutDashboard, FolderKanban, Users, Users2, Bell, LogOut, Menu, X, Settings, BarChart3, Lock } from "lucide-react";
import NotificationsBell from "./NotificationsBell";
import { api } from "../lib/api";

const staffNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
  // Admin Overview sits right after Projects — visible to admins only.
  { to: "/admin-overview", label: "Admin Overview", icon: BarChart3, testId: "nav-admin-overview", roles: ["admin"] },
  // Team page is admin/pm only — designers won't see the nav item.
  { to: "/team", label: "Team", icon: Users, testId: "nav-team", roles: ["admin", "pm"] },
  { to: "/contractors", label: "Contractors", icon: Users2, testId: "nav-contractors" },
];

export default function AppShell({ children }) {
  const { user, workspace, logout } = useAuth();
  const { settings } = usePlatform();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [renewalStatus, setRenewalStatus] = useState(null);

  // Only firm-side users (admin/pm/designer) have a workspace subscription
  // to check. Fetch it once so we can render a read-only banner when the
  // firm's subscription has expired.
  useEffect(() => {
    if (!user || !["admin", "pm", "designer"].includes(user.role)) return;
    let cancelled = false;
    api.get("/workspace/subscription")
      .then((r) => { if (!cancelled) setRenewalStatus(r.data?.renewal_status || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  // Firm branding takes precedence over platform branding for firm staff
  const brand = workspace?.name || settings.company_name || "OrkaiFlow";
  const logo = workspace?.logo_url || settings.logo_url;
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex bg-[#FDFBF7]">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#FDFBF7]/95 backdrop-blur border-b border-[#EBE5D9] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt={brand} className="w-8 h-8 rounded-xl object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-xl bg-[#E07A5F] flex items-center justify-center text-white font-display font-bold">{brand.slice(0, 1).toUpperCase()}</div>
          )}
          <span className="font-display text-lg font-semibold text-[#2C2A29]">{brand}</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell testId="notifications-bell-mobile" />
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="p-2 rounded-full hover:bg-[#F7F4EC]">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`${open ? "flex" : "hidden"} lg:flex flex-col fixed lg:sticky top-0 left-0 z-30 w-72 h-screen bg-[#FDFBF7] border-r border-[#EBE5D9] p-6 pt-20 lg:pt-6`}
      >
        <div className="hidden lg:flex items-center gap-3 mb-8 pb-6 border-b border-[#EBE5D9]">
          {logo ? (
            <img src={logo} alt={brand} className="w-10 h-10 rounded-2xl object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-2xl bg-[#E07A5F] flex items-center justify-center text-white font-display font-bold text-lg">{brand.slice(0, 1).toUpperCase()}</div>
          )}
          <div>
            <div className="font-display text-xl font-semibold text-[#2C2A29] leading-tight">{brand}</div>
            {brand !== (settings.company_name || "OrkaiFlow") && (
              <div className="text-xs text-[#7A7571] font-body">on OrkaiFlow</div>
            )}
          </div>
        </div>

        <nav className="flex flex-col gap-2 flex-1">
          {staffNav
            .filter((item) => !item.roles || item.roles.includes(user?.role))
            .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={item.testId}
              className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}
            >
              <item.icon size={18} />
              <span className="font-body">{item.label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/settings"
              onClick={() => setOpen(false)}
              data-testid="nav-settings"
              className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}
            >
              <Settings size={18} />
              <span className="font-body">Settings</span>
            </NavLink>
          )}
        </nav>

        <div className="mt-4 pt-6 border-t border-[#EBE5D9] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F2CC8F] flex items-center justify-center text-[#2C2A29] font-bold">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="font-body text-sm font-semibold text-[#2C2A29] truncate">{user?.name}</div>
            <div className="text-xs text-[#7A7571] capitalize">{user?.role}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="btn-ghost !p-2" title="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-16 lg:pt-0">
        {renewalStatus?.category === "expired" && (
          <div
            className="bg-[#FEE2E2] border-b border-[#FCA5A5] px-4 sm:px-8 lg:px-12 py-3 flex items-center gap-3"
            data-testid="expired-banner"
          >
            <Lock size={16} className="text-[#9F1239] flex-shrink-0" />
            <div className="text-sm text-[#7F1D1D] flex-1">
              <span className="font-semibold">Your subscription has expired.</span> The workspace is in read-only mode. Contact your platform support to renew.
            </div>
          </div>
        )}
        {renewalStatus?.category === "renewal_soon" && (
          <div
            className="bg-[#FEF3C7] border-b border-[#FDE68A] px-4 sm:px-8 lg:px-12 py-3 flex items-center gap-3"
            data-testid="renewal-soon-banner"
          >
            <Bell size={16} className="text-[#92400E] flex-shrink-0" />
            <div className="text-sm text-[#78350F] flex-1">
              <span className="font-semibold">Renewal reminder:</span> Your subscription expires in {renewalStatus.days_left} day{renewalStatus.days_left === 1 ? "" : "s"}. Reach out to your platform contact to arrange payment.
            </div>
          </div>
        )}
        <div className="hidden lg:flex items-center justify-end px-8 py-4 gap-4">
          <NotificationsBell testId="notifications-bell" />
        </div>
        <div className="px-4 sm:px-8 lg:px-12 pb-16">{children}</div>
      </main>
    </div>
  );
}
